import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Construction, DocumentScanner, FilterBAndW, Palette } from "@mui/icons-material";
import { InfoHint } from "../../../components/ui/detailView";
import { useTenantSettings } from "../../../hooks/useTenantSettings";
import { TenantSettings } from "../../../types/tenantSettings";
import { formatNumber } from "../../../utils";
import { SETTINGS_SECTIONS } from "../../tenantConfig/tenantSettings.config";

const COST_SETTINGS = SETTINGS_SECTIONS.profitability;
const COST_ROWS = COST_SETTINGS.groups.flatMap((group) => group.rows);

// The icons these values already wear elsewhere: the counter kinds on an
// equipment's counters tab, and the jobs chip in the grid below.
const COST_ICONS: Partial<Record<keyof TenantSettings, ReactNode>> = {
  bp_usage_cost: <FilterBAndW fontSize="small" />,
  cp_usage_cost: <Palette fontSize="small" />,
  sp_usage_cost: <DocumentScanner fontSize="small" />,
  job_cost: <Construction fontSize="small" />,
};

const CostTile = ({ icon, label, value }: { icon: ReactNode; label: string; value: unknown }) => {
  const isSet = value !== null && value !== undefined && value !== "";

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        p: 1.5,
        minWidth: 0,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: "var(--defaultRadius)",
        bgcolor: "background.paper",
      }}
    >
      <Box
        sx={(theme) => ({
          display: "flex",
          flexShrink: 0,
          p: 1,
          borderRadius: "50%",
          color: "primary.main",
          bgcolor: alpha(theme.palette.primary.main, 0.1),
        })}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
        <Typography
          variant="h6"
          component="div"
          fontWeight="bold"
          sx={{ color: isSet ? "text.primary" : "text.disabled" }}
        >
          {isSet ? formatNumber(value) : "—"}
        </Typography>
      </Box>
    </Box>
  );
};

/**
 * The workspace defaults profitability is computed from, one tile per value,
 * above the figures they produce. Which values (and their labels) come from
 * the same section the toolbar's "Edit profitability settings" form is built
 * from, so the tiles and the form always list the same fields.
 */
export const ProfitabilityCostSettings = () => {
  const { t } = useTranslation();
  const { settings } = useTenantSettings();

  if (!settings) {
    return null;
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, flexShrink: 0 }}>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Typography variant="subtitle1" component="h2" fontWeight="bold">
          {t(COST_SETTINGS.title)}
        </Typography>
        {COST_SETTINGS.description && <InfoHint text={t(COST_SETTINGS.description)} />}
      </Box>
      <Box
        sx={{
          display: "grid",
          // One row of four on a desktop; wraps instead of squeezing the labels.
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: 2,
        }}
      >
        {COST_ROWS.map((row) => (
          <CostTile
            key={String(row.key)}
            icon={COST_ICONS[row.key]}
            label={t(row.label)}
            value={settings[row.key]}
          />
        ))}
      </Box>
    </Box>
  );
};
